import { forwardRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { useStore } from '../../../store/store';
import Iconography from '../../atoms/Iconography/Iconography';
import Typography from '../../atoms/Typography/Typography';
import { useAutoLogout } from '../../../hooks/useAutoLogout';
import styles from './Layout.module.scss';

type NavItem = {
  path: string;
  label: string;
  icon: string;
  dataTestId: string;
};

const NAV_ITEMS: NavItem[] = [
  {
    path: '/cart-onboarding',
    label: 'Cart Onboarding',
    icon: 'cart',
    dataTestId: 'nav-cart-onboarding',
  },
  { path: '/', label: 'Dashboard', icon: 'home', dataTestId: 'nav-dashboard' },
  {
    path: '/feature-flags',
    label: 'Feature Flags',
    icon: 'preferences',
    dataTestId: 'nav-feature-flags',
  },
  {
    path: '/filehub-onboarding',
    label: 'Filehub Onboarding',
    icon: 'file',
    dataTestId: 'nav-filehub-onboarding',
  },
  {
    path: '/rbac-management',
    label: 'RBAC Management',
    icon: 'key',
    dataTestId: 'nav-rbac-management',
  },
];

export type LayoutProps = {
  dataTestId?: string;
  className?: string;
};

/**
 * Layout organism — wraps the full app with a collapsible sidebar and main content area.
 * Sidebar state (collapsed/expanded) is persisted via Zustand store.
 *
 * @component
 * @param {LayoutProps} props
 * @returns {JSX.Element}
 *
 * @example
 * <Layout />
 */
const Layout = forwardRef<HTMLDivElement, LayoutProps>(
  ({ dataTestId = 'layout', className }, ref) => {
    const isSidebarCollapsed = useStore((state) => state.isSidebarCollapsed);
    const setSidebarCollapsed = useStore((state) => state.setSidebarCollapsed);
    const user = useStore((state) => state.user);
    const logout = useStore((state) => state.logout);
    const navigate = useNavigate();

    useAutoLogout();

    const handleSignOut = () => {
      logout();
      navigate('/login');
    };

    return (
      <div ref={ref} className={classnames(styles.root, className)} data-testid={dataTestId}>
        {/* Sidebar */}
        <aside
          className={classnames(styles.sidebar, {
            [styles.collapsed]: isSidebarCollapsed,
          })}
          data-testid="layout-sidebar"
        >
          {/* Sidebar header — logo + toggle */}
          <div className={styles.sidebarHeader}>
            <div className={styles.logoArea}>
              {!isSidebarCollapsed ? (
                <div className={styles.logoGroup}>
                  <div className={styles.logoIcons}>
                    <Iconography
                      category="brand"
                      name="logo"
                      color="white"
                      size={32}
                      dataTestId="sidebar-logo"
                    />
                    <Iconography
                      category="brand"
                      name="bear"
                      color="white"
                      size={32}
                      dataTestId="sidebar-bear"
                    />
                  </div>
                  <Typography variant="eyebrow" size="large" color="white">
                    Enterprise Tools
                  </Typography>
                </div>
              ) : (
                <Iconography
                  category="brand"
                  name="lucid-logo"
                  color="white"
                  size={32}
                  dataTestId="sidebar-logo-collapsed"
                />
              )}
            </div>
          </div>
          <button
            type="button"
            className={styles.collapseBtn}
            onClick={() => setSidebarCollapsed(!isSidebarCollapsed)}
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidebar-toggle"
          >
            <Iconography
              category="arrows"
              name={isSidebarCollapsed ? 'chevron-right' : 'chevron-left'}
              color="white"
              size={16}
              dataTestId="sidebar-toggle-icon"
            />
          </button>

          {/* Navigation */}
          <nav className={styles.nav} data-testid="sidebar-nav">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  classnames(styles.navItem, { [styles.navItemActive]: isActive })
                }
                data-testid={item.dataTestId}
              >
                <Iconography
                  category="global"
                  name={item.icon}
                  color="white"
                  size={20}
                  dataTestId={`${item.dataTestId}-icon`}
                />
                {!isSidebarCollapsed && (
                  <Typography variant="body" size="small" color="white">
                    {item.label}
                  </Typography>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Sidebar footer — user info + sign out */}
          <div className={styles.sidebarFooter} data-testid="sidebar-footer">
            <div className={styles.userInfo}>
              <Iconography
                category="global"
                name="user"
                color="white"
                dataTestId="sidebar-user-icon"
              />
              {!isSidebarCollapsed && (
                <Typography
                  variant="body"
                  size="small"
                  color="white"
                  data-testid="sidebar-username"
                >
                  {user?.replace(/_/g, ' ')}
                </Typography>
              )}
            </div>
            <button
              type="button"
              className={styles.signOutBtn}
              onClick={handleSignOut}
              data-testid="sidebar-signout"
            >
              <Iconography
                category="global"
                name="logout"
                color="white"
                dataTestId="sidebar-signout-icon"
              />
              {!isSidebarCollapsed && (
                <Typography variant="body" size="small" color="white">
                  Sign Out
                </Typography>
              )}
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className={styles.main} data-testid="layout-main">
          <Outlet />
        </main>
      </div>
    );
  }
);

Layout.displayName = 'Layout';

export default Layout;
